/**
 * StageStrip — the two-stage pipeline spine shared by the Analyses page and
 * Path to GO › Analyse: Stage 1 · Early screening → Stage 2 · Mature
 * validation → Decision. Each segment carries the count, a state chip (when
 * its state has one) and a progress bar; the segment the next step lives in
 * is lit. No prose.
 *
 * States with NO chip (owner, 2026-09-30): a declined screening, a Stage 2
 * waiting on its scenario, and an unpublished decision — the "Screening
 * declined", "Scenario needed" and "Pending" badges were removed. The states
 * themselves still drive the count, fill, lit segment and next step.
 */
import React from 'react';
import { cn } from '@/lib/utils';
import { StatusPill, type StatusPillVariant } from '../../ui/status-pill';
import { screeningScoreLabel, type StageBoardModel, type StageBoardsModel } from '../../../lib/pathToGo/stageBoards';

export interface StageStripDecision {
  /** A band is published — its label and tone. */
  published: boolean;
  label: string;
  tone?: StatusPillVariant;
}

export interface StageStateChip {
  label: string;
  variant: StatusPillVariant;
}

/** The stage's state chip, or null for a state that carries no badge. */
export function stageStateChip(board: StageBoardModel): StageStateChip | null {
  // The counts disclose saved/current currency. Avoid a duplicate warning
  // badge and do not replace it with a success badge for a saved basis.
  if (board.state === 'complete' && board.requiredUsed > board.requiredCurrent
    && board.requiredCurrent < board.requiredTotal
    && !(board.stage === 1 && board.outcome === 'declined')) {
    return null;
  }
  // The screening RESULT outranks "Complete" on Stage 1 — its own vocabulary,
  // never a band: clear / concerns. A declined screening has no chip — and
  // never falls through to a green "Complete".
  if (board.stage === 1 && board.state === 'complete' && board.outcome) {
    // 'clear' is neutral, not the GO green: a screening result is never a band.
    if (board.outcome === 'clear') return { label: 'Screened · clear', variant: 'neutral' };
    if (board.outcome === 'concerns') return { label: 'Screened · concerns', variant: 'warn' };
    return null;
  }
  switch (board.state) {
    case 'complete': return { label: 'Complete', variant: 'success' };
    case 'nothing-required': return { label: 'Nothing required', variant: 'neutral' };
    case 'running': return { label: 'Analysing', variant: 'custom' };
    // Waiting on the promoted scenario: the count reads "— scenario first" and
    // the scenario step names the move; no badge.
    case 'scenario': return null;
    case 'after-screening': return { label: 'After screening', variant: 'neutral' };
    default: return { label: 'In progress', variant: 'custom' };
  }
}

/**
 * The analyses each stage requires (owner, 2026-09-30: Environmental is Stage 2
 * — "it requires equipment"; Public Sentiment is an additional analysis and
 * belongs to neither stage).
 */
const STAGE_MEMBERS: Record<1 | 2, string> = {
  1: 'Screening Assessment',
  2: 'Generation & Impact · Environmental · Financial · Supply chain · Test fit',
};

function pct(board: StageBoardModel): number {
  if (board.requiredTotal === 0) return board.state === 'complete' || board.state === 'nothing-required' ? 100 : 0;
  if (board.state === 'scenario' || board.state === 'after-screening') return 0;
  return (board.requiredUsed / board.requiredTotal) * 100;
}

const Segment: React.FC<{
  title: string;
  live: boolean;
  chip: StageStateChip | null;
  count: React.ReactNode;
  unit: string;
  fill: number;
  foot: string;
  extra?: string;
  testId: string;
}> = ({ title, live, chip, count, unit, fill, foot, extra, testId }) => (
  <div
    className={cn(
      'relative grid gap-2 border-border-subtle px-4 py-3 md:border-r md:last:border-r-0',
      live && 'bg-primary/[0.06] shadow-[inset_3px_0_0_var(--primary)]',
    )}
    role="listitem"
    data-testid={testId}
    data-live={live ? 'true' : 'false'}
  >
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[12.5px] font-semibold text-text-primary">{title}</span>
      {chip ? <StatusPill variant={chip.variant}>{chip.label}</StatusPill> : null}
    </div>
    <div className="text-[22px] font-semibold leading-none tabular-nums text-text-primary">
      {count}
      <span className="ml-1.5 text-[11px] font-normal uppercase tracking-[0.08em] text-text-muted">{unit}</span>
    </div>
    <div className="h-1 overflow-hidden bg-border-subtle" aria-hidden>
      <div className="h-full bg-primary transition-[width] duration-500" style={{ width: `${fill}%` }} />
    </div>
    <div className="text-[11px] text-text-muted">{foot}</div>
    {extra ? <div className="text-[11px] tabular-nums text-text-secondary" data-testid={`${testId}-score`}>{extra}</div> : null}
  </div>
);

export const StageStrip: React.FC<{
  model: StageBoardsModel;
  decision?: StageStripDecision;
  className?: string;
  'data-testid'?: string;
}> = ({ model, decision, className, 'data-testid': testId = 'stage-strip' }) => {
  const { screening, validation, next } = model;
  const live1 = next.kind === 'analysis' && next.row.stage === 1;
  const live2 = next.kind === 'scenario' || (next.kind === 'analysis' && next.row.stage === 2);
  const liveDecision = next.kind === 'decision';
  const countOf = (board: StageBoardModel): React.ReactNode =>
    board.state === 'scenario' || board.state === 'after-screening'
      ? '—'
      : board.requiredTotal === 0 ? '—' : `${board.requiredUsed} / ${board.requiredTotal}`;
  // Freshness is disclosed after the used count; it never erases a successful
  // saved result or makes a completed stage read as empty.
  const unitOf = (board: StageBoardModel): string =>
    board.state === 'scenario' ? 'scenario first'
      : board.state === 'after-screening' ? 'after screening'
        : board.requiredTotal === 0
          ? 'nothing required'
          : `used${board.requiredCurrent < board.requiredUsed ? ` · ${board.requiredCurrent} current` : ''}`;
  return (
    <div
      className={cn('grid border border-border-subtle bg-card md:grid-cols-[1fr_1fr_0.7fr]', className)}
      role="list"
      aria-label="Decision pipeline"
      data-testid={testId}
    >
      <Segment
        title={screening.label}
        live={live1}
        chip={stageStateChip(screening)}
        count={countOf(screening)}
        unit={unitOf(screening)}
        fill={pct(screening)}
        foot={STAGE_MEMBERS[1]}
        extra={screening.score != null ? screeningScoreLabel(screening.score, screening.scoreCoverage, screening.scoreBasis) : undefined}
        testId={`${testId}-1`}
      />
      <Segment title={validation.label} live={live2} chip={stageStateChip(validation)} count={countOf(validation)} unit={unitOf(validation)} fill={pct(validation)} foot={STAGE_MEMBERS[2]} testId={`${testId}-2`} />
      <Segment
        title="Decision"
        live={liveDecision}
        chip={decision?.published ? { label: decision.label, variant: decision.tone ?? 'success' } : null}
        count={decision?.published ? 'Published' : '—'}
        unit=""
        fill={decision?.published ? 100 : 0}
        foot={decision?.published ? 'Both stages complete' : 'Publishes when both stages are complete'}
        testId={`${testId}-decision`}
      />
    </div>
  );
};

export default StageStrip;
